/**
 * Nebula Response Parser
 *
 * Parses the fully-accumulated streamed text from the backend and extracts
 * a structured artifact object.
 *
 * Detection priority:
 *   1. ```html / html-viz / html-live  → { type: 'html',    content }
 *   2. ```react-viz / jsx-live         → { type: 'react',   content }
 *   3. ```physics-viz / json-viz       → { type: 'plotly',  content }
 *   4. ```mermaid                      → { type: 'mermaid', content }
 *   4.5. ```markmap                    → { type: 'markmap', content }
 *   5. Firebase/HTTPS image URL        → { type: 'image',   content }
 *      (extensions: png, jpg, jpeg, gif, webp)
 *   6. Firebase/HTTPS video URL        → { type: 'video',   content }
 *      (extensions: mp4, webm, mov)
 *   7. Firebase/HTTPS audio URL        → { type: 'audio',   content }
 *      (extensions: mp3, wav, ogg, aac, m4a)
 *   8. Fallback                        → { type: 'text',    content }
 */

const HTML_FENCE_RE   = /```(?:html-viz|html-live|html)\s*([\s\S]*?)```/i;
const REACT_FENCE_RE  = /```(?:react-viz|jsx-live)\s*([\s\S]*?)```/i;
const PLOTLY_FENCE_RE = /```(?:physics-viz|json-viz)\s*([\s\S]*?)```/i;
const MERMAID_FENCE_RE = /```mermaid\s*([\s\S]*?)```/i;
const MARKMAP_FENCE_RE = /```markmap\s*([\s\S]*?)```/i;
const IMAGE_URL_RE    = /https?:\/\/\S+\.(?:png|jpg|jpeg|gif|webp)(?:\?\S*)?/i;
const VIDEO_URL_RE    = /https?:\/\/\S+\.(?:mp4|webm|mov)(?:\?\S*)?/i;
const AUDIO_URL_RE    = /https?:\/\/\S+\.(?:mp3|wav|ogg|aac|m4a)(?:\?\S*)?/i;

const MULTI_FENCE_RE = /```(html-viz|html-live|html|react-viz|jsx-live|physics-viz|json-viz|mermaid|markmap)\s*([\s\S]*?)```/gi;
const ASSET_MARKER_RE = /!\[ASSET\]\(([^)\s]+)\)/gi;
const MARKDOWN_MEDIA_RE = /!\[[^\]]*\]\((https?:\/\/[^)\s]+\.(?:png|jpg|jpeg|gif|webp|mp4|webm|mov|mp3|wav|ogg|aac|m4a)(?:\?[^)\s]*)?)\)/gi;
const BARE_MEDIA_URL_RE = /https?:\/\/\S+\.(?:png|jpg|jpeg|gif|webp|mp4|webm|mov|mp3|wav|ogg|aac|m4a)(?:\?\S*)?/gi;

function fenceTagToType(tag) {
  const lower = (tag || "").toLowerCase();
  if (lower === "html" || lower === "html-viz" || lower === "html-live") return "html";
  if (lower === "react-viz" || lower === "jsx-live") return "react";
  if (lower === "physics-viz" || lower === "json-viz") return "plotly";
  if (lower === "mermaid") return "mermaid";
  if (lower === "markmap") return "markmap";
  return "text";
}

function urlToMediaType(url) {
  const clean = (url || "").toLowerCase().split("?")[0];
  const ext = clean.split(".").pop();
  if (["mp4", "webm", "mov"].includes(ext)) return "video";
  if (["mp3", "wav", "ogg", "aac", "m4a"].includes(ext)) return "audio";
  return "image";
}

/**
 * Extract all artifacts (fenced blocks, canonical ![ASSET](...) markers, and
 * media URLs) in document order. Each entry includes its own `preamble` and
 * `postamble` plus a 0-based `typeOccurrence` index among fences of the same
 * type (used by `buildUpdatedNebulaContent` when saving edits to a specific tab).
 */
function extractAllArtifacts(text) {
  if (!text) return [];
  const rawMatches = [];

  const overlaps = (start, end) =>
    rawMatches.some((m) => start < m.end && end > m.start);

  // 1. All fenced code/diagram blocks
  let m;
  MULTI_FENCE_RE.lastIndex = 0;
  while ((m = MULTI_FENCE_RE.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    rawMatches.push({
      start,
      end,
      type: fenceTagToType(m[1]),
      langTag: m[1],
      content: m[2].trim(),
      isStoragePath: false,
    });
  }

  // 2. Canonical ![ASSET](asset_...) markers outside fenced blocks
  ASSET_MARKER_RE.lastIndex = 0;
  while ((m = ASSET_MARKER_RE.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    if (overlaps(start, end)) continue;
    const assetId = m[1].trim();
    rawMatches.push({
      start,
      end,
      type: urlToMediaType(assetId),
      content: assetId,
      isStoragePath: true,
    });
  }

  // 3. Markdown media ![...](https://...) outside fenced blocks
  MARKDOWN_MEDIA_RE.lastIndex = 0;
  while ((m = MARKDOWN_MEDIA_RE.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    if (overlaps(start, end)) continue;
    const url = m[1].trim();
    rawMatches.push({
      start,
      end,
      type: urlToMediaType(url),
      content: url,
      isStoragePath: false,
    });
  }

  // 4. Bare media URLs outside fenced blocks and markdown image syntax
  BARE_MEDIA_URL_RE.lastIndex = 0;
  while ((m = BARE_MEDIA_URL_RE.exec(text)) !== null) {
    const start = m.index;
    let rawUrl = m[0];
    // Strip trailing markdown/punctuation characters like `)` or `,`
    rawUrl = rawUrl.replace(/[),.;]+$/, "");
    const end = start + rawUrl.length;
    if (overlaps(start, end)) continue;
    rawMatches.push({
      start,
      end,
      type: urlToMediaType(rawUrl),
      content: rawUrl,
      isStoragePath: false,
    });
  }

  rawMatches.sort((a, b) => a.start - b.start);

  const typeCounts = {};
  return rawMatches.map((item, idx) => {
    const occ = typeCounts[item.type] ?? 0;
    typeCounts[item.type] = occ + 1;
    return {
      type: item.type,
      content: item.content,
      ...(item.isStoragePath ? { isStoragePath: true } : {}),
      ...(item.langTag ? { langTag: item.langTag } : {}),
      typeOccurrence: occ,
      artifactIndex: idx,
      preamble: text.slice(0, item.start).trim(),
      postamble: text.slice(item.end).trim(),
    };
  });
}

/**
 * @param {string} text - Full streamed response text
 * @returns {{ type: 'html'|'react'|'plotly'|'mermaid'|'image'|'video'|'text', content: string, preamble?: string, postamble?: string, items?: Array } | null}
 */
function _parseNebulaResponseImpl(text) {
  if (!text) return null;

  const allItems = extractAllArtifacts(text);

  // Helper: attach multi-artifact items list when found
  const withItems = (primary) => {
    if (allItems.length > 0) {
      return { ...primary, items: allItems };
    }
    return primary;
  };

  // Helper: extract surrounding text for fenced/inline matches.
  // preamble = text before the match; postamble = text after the match.
  const surroundings = (match) => ({
    preamble: text.slice(0, match.index).trim(),
    postamble: text.slice(match.index + match[0].length).trim(),
  });

  // 1. HTML fenced code block
  const htmlMatch = HTML_FENCE_RE.exec(text);
  if (htmlMatch) {
    return withItems({ type: "html", content: htmlMatch[1].trim(), ...surroundings(htmlMatch) });
  }

  // 2. React / JSX component
  const reactMatch = REACT_FENCE_RE.exec(text);
  if (reactMatch) {
    return withItems({ type: "react", content: reactMatch[1].trim(), ...surroundings(reactMatch) });
  }

  // 3. Plotly JSON chart
  const plotlyMatch = PLOTLY_FENCE_RE.exec(text);
  if (plotlyMatch) {
    return withItems({ type: "plotly", content: plotlyMatch[1].trim(), ...surroundings(plotlyMatch) });
  }

  // 4. Mermaid diagram
  const mermaidMatch = MERMAID_FENCE_RE.exec(text);
  if (mermaidMatch) {
    return withItems({ type: "mermaid", content: mermaidMatch[1].trim(), ...surroundings(mermaidMatch) });
  }

  // 4.5. Markmap mind map
  const markmapMatch = MARKMAP_FENCE_RE.exec(text);
  if (markmapMatch) {
    return withItems({ type: "markmap", content: markmapMatch[1].trim(), ...surroundings(markmapMatch) });
  }

  // 5. Image URL
  const imageMatch = IMAGE_URL_RE.exec(text);
  if (imageMatch) {
    return withItems({ type: "image", content: imageMatch[0], ...surroundings(imageMatch) });
  }

  // 7. Video URL
  const videoMatch = VIDEO_URL_RE.exec(text);
  if (videoMatch) {
    return withItems({ type: "video", content: videoMatch[0], ...surroundings(videoMatch) });
  }

  // 8. Audio URL
  const audioMatch = AUDIO_URL_RE.exec(text);
  if (audioMatch) {
    return withItems({ type: "audio", content: audioMatch[0], ...surroundings(audioMatch) });
  }

  // 8.5. Canonical ![ASSET](...) markers when no direct URL/fence matched
  if (allItems.length > 0) {
    const first = allItems[0];
    return {
      ...first,
      items: allItems,
    };
  }

  // 9. Plain text — always return the full response so canvas never shows empty
  return { type: "text", content: text.trim() };
}

// ---------------------------------------------------------------------------
// Memoization
// ---------------------------------------------------------------------------
// Callers (e.g. MainContent.js) re-invoke this on every render with the same
// answer.content string. Without memoization each call returns a fresh object,
// causing downstream `useEffect(..., [artifact])` hooks to re-fire and reset
// editor state (e.g. inline AI edit diff gate). Cache the last few inputs so
// identical content yields a stable object reference.
const _parseCache = new Map(); // text -> result
const _PARSE_CACHE_MAX = 8;

export function parseNebulaResponse(text) {
  if (!text) return null;
  if (_parseCache.has(text)) return _parseCache.get(text);
  const result = _parseNebulaResponseImpl(text);
  // Simple FIFO eviction — cache holds the N most recent unique inputs.
  if (_parseCache.size >= _PARSE_CACHE_MAX) {
    const firstKey = _parseCache.keys().next().value;
    _parseCache.delete(firstKey);
  }
  _parseCache.set(text, result);
  return result;
}
